/**
 * Product terminology (F34).
 *
 * One list, three consumers that used to disagree with each other:
 *
 *   1. the RUNTIME translator (`apps/brain-service` — guide copy, catalogues),
 *   2. the BUILD-TIME locale pipeline (`packages/locale/scripts`),
 *   3. the banned-term scan (`scripts/qa/check-terminology.mjs`).
 *
 * Before this existed, (1) and (2) each carried their own brand list — kept in
 * step by a drift spec — and NEITHER had any notion of an approved per-locale
 * rendering. So the model picked a generic word, the two layers drifted from
 * each other and from the navigation, and "Household" shipped to production as
 * "Doméstico" (a domestic worker) on the TelePortal setup guide.
 *
 * Lives in `@tonnie/foundation` rather than `@tonnie/locale` because
 * `@tonnie/locale` is bundler-only: brain-service is a NestJS app built with
 * `@nx/js:tsc` and cannot import it (DW6-1 / H9). A wildcard subpath export
 * would also hit nx's exact-match `NX_MAPPINGS` trap.
 *
 * @see terminology.json for the data and the rules for editing it.
 */

import terminologyJson from './terminology.json';

// ═══════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════

/** Locales the terminology list covers. `en` is the source, never a target. */
export type TerminologyLocale = 'pt-PT' | 'pt-BR' | 'es-ES' | 'it-IT';

export const TERMINOLOGY_LOCALES: readonly TerminologyLocale[] = [
  'pt-PT',
  'pt-BR',
  'es-ES',
  'it-IT',
];

/** How a term is rendered in one locale. */
export interface TerminologyLocaleRule {
  /** The one approved rendering. */
  readonly approved: string;
  /** Renderings that are wrong here. Checked only when the English matches. */
  readonly banned: readonly string[];
  /** When true the locale value must EQUAL `approved` (key-scoped entries). */
  readonly exact?: boolean;
}

export interface TerminologyTerm {
  readonly id: string;
  /** `brand` is masked and never translated; `term` has a per-locale rendering. */
  readonly kind: 'brand' | 'term';
  /**
   * Revision count. `0` = unchanged since the caches were populated and
   * therefore contributes NOTHING to a cache key. See `terminologyCacheTag`.
   */
  readonly rev: number;
  /** English source pattern, matched whole-word and case-insensitively. */
  readonly match: string;
  /** `text` (default) matches source text; `keys` matches locale key paths. */
  readonly scope?: 'text' | 'keys';
  /**
   * A rule about how a TRANSLATION is written (register, regional vocabulary)
   * rather than how a term is rendered. The scan applies it to locale values
   * only, never to an English source config, which may quote the banned word.
   */
  readonly translationOnly?: boolean;
  /** For `scope: 'keys'` — the exact dot-paths this rule governs. */
  readonly keys?: readonly string[];
  readonly note?: string;
  /** Per-locale rules. A locale without an entry is not governed by this term. */
  readonly locales?: Readonly<Partial<Record<TerminologyLocale, TerminologyLocaleRule>>>;
}

interface TerminologyFile {
  readonly README: readonly string[];
  readonly terms: readonly TerminologyTerm[];
}

// ═══════════════════════════════════════════════════════════════════════
// THE LIST
// ═══════════════════════════════════════════════════════════════════════

export const TERMINOLOGY: readonly TerminologyTerm[] = (terminologyJson as TerminologyFile).terms;

/**
 * Brand / product names that must survive a translation verbatim.
 *
 * Longest-first so multi-word names ("Tonnie Checked") mask before "Tonnie".
 * This replaces the two hand-maintained `GLOSSARY` arrays — one in
 * `apps/brain-service`, one in `packages/locale/scripts` — and the spec that
 * asserted they matched. That spec is deleted rather than kept: a drift test
 * for two copies that no longer exist is a gate that can never fail.
 */
export const BRAND_TERMS: readonly string[] = TERMINOLOGY.filter(
  (term) => term.kind === 'brand'
).map((term) => term.match);

/** Terms that carry an approved per-locale rendering, matched against text. */
export const TEXT_TERMS: readonly TerminologyTerm[] = TERMINOLOGY.filter(
  (term) => term.kind === 'term' && (term.scope ?? 'text') === 'text'
);

/** Terms governed by an exact set of locale key paths rather than by text. */
export const KEY_SCOPED_TERMS: readonly TerminologyTerm[] = TERMINOLOGY.filter(
  (term) => term.kind === 'term' && term.scope === 'keys'
);

// ═══════════════════════════════════════════════════════════════════════
// MATCHING
// ═══════════════════════════════════════════════════════════════════════

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Whole-word matcher for a source pattern.
 *
 * `\b` on both ends, so "Career" does not trip the "Care" rule, and "households"
 * does NOT match "household" (deliberate — a plural gets its own entry if it
 * needs one, rather than being silently swept in).
 *
 * CASE SENSITIVITY IS THE DIFFERENCE BETWEEN A GATE AND A WALL OF NOISE:
 *
 *   - A BRAND is matched case-SENSITIVELY, exactly as `maskBrands` does. Half
 *     the brand list is also ordinary English — Care, Career, Citizen,
 *     Passport, Tonnie aside — and a case-insensitive check reports "cuidado"
 *     for every sentence about care, "carreira" for every sentence about a
 *     career: 1 927 findings, of which 281 are real. A gate at that signal
 *     ratio gets switched off, which is how brand drift ended up warn-only in
 *     the first place.
 *   - A TERM is matched case-INSENSITIVELY, because it is prose: "billing" and
 *     "Billing" are the same word and both must render as the approved one.
 */
const matcherCache = new Map<string, RegExp>();
function matcherFor(pattern: string, caseSensitive: boolean): RegExp {
  const cacheKey = `${caseSensitive ? 'S' : 'I'}:${pattern}`;
  let regex = matcherCache.get(cacheKey);
  if (!regex) {
    // Letter boundaries, not `\b`: `\b` is ASCII-only and never finds the end
    // of a word ending in an accented letter (`\bvocê\b` does not match
    // "você precisa"). Banned renderings are Portuguese, Spanish and Italian.
    regex = new RegExp(
      `(?<![\\p{L}\\p{N}_])${escapeRegExp(pattern)}(?![\\p{L}\\p{N}_])`,
      caseSensitive ? 'u' : 'iu'
    );
    matcherCache.set(cacheKey, regex);
  }
  return regex;
}

/** True when `term`'s English pattern occurs in `text`. */
export function termOccursIn(term: TerminologyTerm, text: string): boolean {
  return matcherFor(term.match, term.kind === 'brand').test(text);
}

/** Terms whose English pattern occurs in `text`, ordered as declared. */
export function matchTerms(text: string): readonly TerminologyTerm[] {
  return TERMINOLOGY.filter(
    (term) => (term.scope ?? 'text') === 'text' && termOccursIn(term, text)
  );
}

// ═══════════════════════════════════════════════════════════════════════
// CACHE KEYING (DW6-2)
// ═══════════════════════════════════════════════════════════════════════

/**
 * The terminology component of a translation cache key for one source string —
 * or the EMPTY STRING when no revised term appears in it.
 *
 * This is the whole point of the design, so it is worth being blunt about it:
 *
 *   - Both caches key on a hash of the raw source text. Changing an approved
 *     rendering therefore invalidates NOTHING; the old Portuguese is served
 *     until the 30-day Redis TTL expires or the source English changes.
 *   - The only lever that existed was a global prefix bump, which re-translates
 *     every cached string in every locale — about 8 220 model calls, including
 *     the entire 1 809-string QultureUp catalogue — to correct a few words.
 *   - Appending the revision of only the terms that actually OCCUR in the string
 *     costs about 124 calls instead.
 *
 * An entry at `rev: 0` contributes nothing, and a string containing no revised
 * term returns `''`, so the caller appends nothing and the key stays
 * byte-identical to the one already in Redis. That is not a detail: a segment
 * that is always present (even a constant `:t0`) changes every key at once and
 * is a full flush wearing a different name.
 *
 * Stable and order-independent: segments are sorted by id, so reordering
 * `terminology.json` cannot silently invalidate the cache.
 *
 * SCOPED TO THE TARGET LOCALE. A revision only makes stale the translations
 * INTO a locale the term governs: a `term` governs the locales its `locales`
 * map lists, a `brand` governs every locale (it must survive verbatim in all of
 * them). Without the target, a pt-PT-only rule invalidated the es-ES, it-IT and
 * pt-BR translations of every string it matched, whose wording it never
 * changed — and the pt-PT register rule (`pt-pt-register-you`, English "you")
 * matches most of the catalogue, so that was a near-total flush of three
 * locales wearing a scoped name. Into pt-PT the same strings DO carry the tag:
 * their cached translations were written in the Brazilian "você" register the
 * rule exists to remove, and re-translating them is the fix, not a side effect.
 */
export function terminologyCacheTag(text: string, targetLocale: string): string {
  const segments = matchTerms(text)
    .filter((term) => term.rev > 0 && governsLocale(term, targetLocale))
    .map((term) => `${term.id}.${term.rev}`)
    .sort();
  return segments.length === 0 ? '' : `:t/${segments.join('+')}`;
}

/** Does a revision of `term` change how text is rendered into `locale`? */
function governsLocale(term: TerminologyTerm, locale: string): boolean {
  if (term.kind === 'brand') return true;
  return term.locales?.[locale as TerminologyLocale] !== undefined;
}

// ═══════════════════════════════════════════════════════════════════════
// PROMPTING
// ═══════════════════════════════════════════════════════════════════════

/**
 * Terminology instructions for one target locale, as prompt lines — empty when
 * no governed term appears in the batch, so short generic strings are not
 * charged for a glossary they cannot use.
 *
 * Only terms actually present in `texts` are listed. Sending the whole list on
 * every call both wastes tokens and dilutes the instruction the model needs.
 */
export function terminologyPromptLines(texts: readonly string[], locale: string): string[] {
  const seen = new Set<string>();
  const lines: string[] = [];
  for (const text of texts) {
    for (const term of matchTerms(text)) {
      if (term.kind !== 'term' || seen.has(term.id)) continue;
      const rule = term.locales?.[locale as TerminologyLocale];
      if (!rule) continue;
      seen.add(term.id);
      const banned =
        rule.banned.length > 0 ? ` Never render it as: ${rule.banned.join(', ')}.` : '';
      lines.push(`- "${term.match}" is always "${rule.approved}" in this locale.${banned}`);
    }
  }
  return lines;
}

// ═══════════════════════════════════════════════════════════════════════
// SCANNING
// ═══════════════════════════════════════════════════════════════════════

export interface TerminologyViolation {
  readonly locale: string;
  readonly keyPath: string;
  readonly termId: string;
  readonly approved: string;
  readonly found: string;
  readonly value: string;
}

/**
 * Check one translated string against the terminology.
 *
 * CONDITIONED ON THE ENGLISH SOURCE, which is what makes the scan usable rather
 * than a wall of false positives: pt-PT may not say "cobrança" where the English
 * says "billing", but "First Charge" → "Primeira Cobrança" is correct and must
 * stay legal. A scan that only looked at the translated value could not tell
 * those apart and would have been switched off within a week.
 */
export function checkTerminology(
  locale: string,
  keyPath: string,
  englishSource: string,
  translated: string
): TerminologyViolation[] {
  const violations: TerminologyViolation[] = [];

  for (const term of TERMINOLOGY) {
    const rule = term.locales?.[locale as TerminologyLocale];
    const keyScoped = term.scope === 'keys';

    if (term.kind === 'brand') {
      // A brand must survive verbatim whenever the English carries it.
      if (termOccursIn(term, englishSource) && !translated.includes(term.match)) {
        violations.push({
          locale,
          keyPath,
          termId: term.id,
          approved: term.match,
          found: translated,
          value: translated,
        });
      }
      continue;
    }

    if (!rule) continue;

    if (keyScoped) {
      if (!term.keys?.includes(keyPath)) continue;
      if (rule.exact === true && translated !== rule.approved) {
        violations.push({
          locale,
          keyPath,
          termId: term.id,
          approved: rule.approved,
          found: translated,
          value: translated,
        });
      }
      continue;
    }

    if (!termOccursIn(term, englishSource)) continue;
    for (const banned of rule.banned) {
      if (matcherFor(banned, false).test(translated)) {
        violations.push({
          locale,
          keyPath,
          termId: term.id,
          approved: rule.approved,
          found: banned,
          value: translated,
        });
        break;
      }
    }
  }

  return violations;
}
